import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

export interface PieSlice {
  /** Display label (e.g. category name). */
  label: string;
  /** Non-negative count; zero-valued slices are dropped before rendering. */
  value: number;
}

interface SimplePieChartProps {
  data: PieSlice[];
  /** Pixel size of the SVG square. Default 180. */
  size?: number;
  /** Empty-state copy shown when every slice is zero. */
  emptyLabel?: string;
}

/**
 * Tiny dependency-free pie chart. Rendered with SVG `path`s — one wedge per
 * slice, sorted by `value` desc so the largest segment starts at 12 o'clock
 * and the legend reads top-to-bottom. Suitable for ~3-15 slices; outside
 * that range you'll want a real chart library.
 *
 * Colours come from a small fixed palette tuned for accessible contrast on
 * the app's light background; if there are more slices than colours, the
 * palette wraps (acceptable for the typical use case of 5-10 categories).
 */
const PALETTE = [
  '#1f6feb', // primary blue
  '#6f42c1', // secondary purple
  '#1aaf6c', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#0ea5e9', // sky
  '#8b5cf6', // violet
  '#14b8a6', // teal
  '#f97316', // orange
  '#84cc16', // lime
];

export function SimplePieChart({ data, size = 180, emptyLabel = 'No data' }: SimplePieChartProps) {
  // Sort slices largest → smallest and drop zeros so the chart matches the
  // sorted-DESC tables next to it.
  const slices = useMemo(
    () => data.filter((s) => s.value > 0).sort((a, b) => b.value - a.value),
    [data],
  );

  const total = useMemo(() => slices.reduce((acc, s) => acc + s.value, 0), [slices]);

  if (slices.length === 0 || total === 0) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: '50%',
        }}
      >
        <Typography variant="caption">{emptyLabel}</Typography>
      </Box>
    );
  }

  // Edge case: a single-slice pie can't be drawn as an SVG arc (start and
  // end points would coincide), so render it as a plain filled circle.
  const single = slices.length === 1;
  const radius = size / 2;
  const cx = radius;
  const cy = radius;

  let cumulative = 0;
  const wedges = slices.map((slice, idx) => {
    const startAngle = (cumulative / total) * Math.PI * 2 - Math.PI / 2; // start at 12 o'clock
    cumulative += slice.value;
    const endAngle = (cumulative / total) * Math.PI * 2 - Math.PI / 2;
    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    const d = [
      `M ${cx} ${cy}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
      'Z',
    ].join(' ');
    return { d, color: PALETTE[idx % PALETTE.length], slice };
  });

  return (
    <Stack direction="row" spacing={3} alignItems="center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {single ? (
          <circle cx={cx} cy={cy} r={radius} fill={PALETTE[0]} />
        ) : (
          wedges.map((w, i) => <path key={i} d={w.d} fill={w.color} />)
        )}
      </svg>
      <Stack spacing={0.5}>
        {wedges.map((w, i) => {
          const pct = ((w.slice.value / total) * 100).toFixed(0);
          return (
            <Stack key={i} direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  backgroundColor: w.color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ minWidth: 120 }} noWrap>
                {w.slice.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {w.slice.value} ({pct}%)
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
}
